import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import * as DocumentReader from "../DocumentReader";
import { EPubView } from "../EPubReader/EPubView";
import { ExternalView } from "../Reader/ExternalView";
import { ImageView } from "../Reader/ImageView";
import { PdfView } from "../Reader/PdfView";
import { UnknownView } from "../Reader/UnknownView";
import { HtmlView } from "../Reader/HtmlView";
import { AppStore } from "../Stores/AppStore";
import { DialogStore } from "../Stores/DialogStore";
import { DocumentFilter } from "../Stores/DocumentFilterStore";
import { DocumentSearchStore } from "../Stores/DocumentSearchStore";
import { RecentFilesDataStore } from "../Stores/RecentFilesDataStore";
import { Strings } from "../Stores/StringsStore";
import { UiRepos } from "../Stores/UiStore";
import { VideoView } from "../Video/VideoView";

interface IDocumentViewRouteProps extends RouteComponentProps<IDocumentViewPropsParams> { }

interface IDocumentViewPropsParams {
	id: string;
	lang: string;
	file: string;
}

@observer
export class DocumentView extends React.Component<IDocumentViewRouteProps, {}> {

	@observable store: DocumentSearchStore = new DocumentSearchStore(DocumentFilter);
	@observable id: string;
	ext: string;
	@observable isDeleted: boolean;
	@observable originalFileName: string;
	@observable docId: string;
	@observable docLanguage: string;
	@observable collectionId: string;
	@observable downloadName: string;
	@observable canDownload: boolean;
	@observable coverAvailable: boolean;
	@observable mimeType: string;
	@observable launchFile: string;
	@observable name: string;
	isBusy: boolean = false;


	componentWillMount() {
		this.init(this.props);
		this.store.loadUiSettings();
		this.store.areaUiInfo.contentGroupAccordionStatus.replace(UiRepos.readerUiInfo.contentGroupAccordionStatus);
	}

	componentWillUpdate(props: IDocumentViewRouteProps) {
		this.init(props);
	}

	init(props: IDocumentViewRouteProps) {
		if (!this.isBusy) {
			this.isBusy = true;
			let dot = props.match.params.file.lastIndexOf(".");
			this.ext = (dot > 0) ? props.match.params.file.substr(dot) : undefined;
			if (props.location.pathname.split('/').length > 3) {
				const shareLink: DocumentReader.ShareFileQuery = new DocumentReader.ShareFileQuery();
				shareLink.documentId = decodeURIComponent(props.match.params.id);
				shareLink.originalFileName = decodeURIComponent(props.match.params.file);
				shareLink.documentLanguage = decodeURIComponent(props.match.params.lang);

				DocumentReader.getFileGuidAsync(shareLink)
					.then(result => {
						this.isBusy = false;
						if (result !== null) {
							this.id = result;
							let fileIds: string[] = [this.id];
							this.getFilesFromDocument(fileIds);
						}
					})
					.catch(error => { DialogStore.showError(Strings.general.errorDialogTitle, error); this.isBusy = false; });
			}
			else {
				this.id = (dot > 0) ? props.match.params.file.substr(0, dot) : props.match.params.file;
				AppStore.setTitle(this.props.match.params.file);
				let fileIds: string[] = [this.id];
				this.getFilesFromDocument(fileIds);
			}
		}
	}

	getFilesFromDocument = (fileIds: string[]) => {
		DocumentReader.getDocumentFilesAsync(fileIds)
			.then(result => {
				if (result.length === 1) {
					AppStore.setTitle(result[0].name);
				}
			})
			.catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
		DocumentReader.getFileViewDetailsAsync(this.id)
			.then(result => {
				this.docId = result.documentId;
				this.docLanguage = result.language;
				this.collectionId = result.collectionId;
				this.downloadName = result.downloadName;
				this.canDownload = result.canDownload;
				this.coverAvailable = result.isCoverAvailable;
				this.mimeType = result.mimeType;
				this.launchFile = result.launchFile;
				this.name = result.name;
				this.originalFileName = result.originalFilename;
				this.isDeleted = result.isDeleted;
				RecentFilesDataStore.addToRecentFiles(DocumentFilter.createFileVariant(this.docId, this.docLanguage));
			})
			.catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
	}

	render() {
		if (this.id && this.id != "") {
			switch (this.ext) {
				case ".mp4":
					return <VideoView
						id={this.id}
						docId={this.docId}
						docLanguage={this.docLanguage}
						collectionId={this.collectionId}
						downloadName={this.downloadName}
						canDownload={this.canDownload}
						fileName={this.name}
						originalFilename={this.originalFileName}
						isDeleted={this.isDeleted}
					/>
				case ".pdf":
					return <PdfView
						id={this.id}
						searchTerm={this.store.searchTerm}
						docId={this.docId}
						docLanguage={this.docLanguage}
						collectionId={this.collectionId}
						downloadName={this.downloadName}
						canDownload={this.canDownload}
						fileName={this.name}
						originalFilename={this.originalFileName}
						isDeleted={this.isDeleted}
					/>
				case ".epub":
					return <EPubView
						id={this.id}
						docId={this.docId}
						docLanguage={this.docLanguage}
						collectionId={this.collectionId}
						downloadName={this.downloadName}
						canDownload={this.canDownload}
						fileName={this.name}
						originalFilename={this.originalFileName}
						isDeleted={this.isDeleted}
					/>
				case ".png":
				case ".jpg":
				case ".jpeg":
				case ".svg":
					return <ImageView
						id={this.id}
						ext={this.ext}
						docId={this.docId}
						docLanguage={this.docLanguage}
						collectionId={this.collectionId}
						downloadName={this.downloadName}
						canDownload={this.canDownload}
						fileName={this.name}
						originalFilename={this.originalFileName}
						isDeleted={this.isDeleted}
					/>
				case ".zip":
					return <HtmlView
						id={this.id}
						docId={this.docId}
						docLanguage={this.docLanguage}
						collectionId={this.collectionId}
						downloadName={this.downloadName}
						canDownload={this.canDownload}
						launchFile={this.launchFile}
						fileName={this.name}
						originalFilename={this.originalFileName}
						isDeleted={this.isDeleted}
					/>
				default:
					return <ExternalView
						id={this.id}
						ext={this.ext}
						docId={this.docId}
						docLanguage={this.docLanguage}
						collectionId={this.collectionId}
						downloadName={this.downloadName}
						canDownload={this.canDownload}
						coverAvailable={this.coverAvailable}
						mimeType={this.mimeType}
						fileName={this.name}
						originalFilename={this.originalFileName}
						isDeleted={this.isDeleted}
					/>
			}
		}
		else {
			return <></>;
		}
	}
}